import { useGetFieldFilterMutation } from "../../../api/dataDictionaryApiSlice";
import { useCallback, useRef, useState } from "react";
import { AgGridReact } from "@ag-grid-community/react";

interface FilterModel {
    filterType: string,
    values: string[]
}

function compareFilterValues(current: Record<string, FilterModel>, updated: Record<string, FilterModel>): string[] {
    const changedFields: string[] = [];
    const allFields = Array.from(new Set([...Object.keys(current), ...Object.keys(updated)]));

    for (const field of allFields) {
        const currentFilter = current[field];
        const updatedFilter = updated[field];

        if (currentFilter && !updatedFilter) {
            changedFields.push(field);
            continue;
        }

        if (!currentFilter && updatedFilter) {
            changedFields.push(field);
            continue;
        }

        if (currentFilter && updatedFilter) {
            if (currentFilter.filterType !== updatedFilter.filterType) {
                changedFields.push(field);
                continue;
            }

            const currentValues = [...currentFilter.values].sort();
            const updatedValues = [...updatedFilter.values].sort();

            if (currentValues.length !== updatedValues.length ||
                !currentValues.every((value, index) => value === updatedValues[index])) {
                changedFields.push(field);
            }
        }
    }

    return changedFields;
}

function createPrimaryFilter(newFilters: Record<string, FilterModel>, primaryFilterRef: React.MutableRefObject<string | null>, filterCacheRef: React.MutableRefObject<Map<string, string[]>>) {
    const activeFilterFields = Object.keys(newFilters);

    if (activeFilterFields.length === 0) {
        if (primaryFilterRef.current) {
            primaryFilterRef.current = null;
        }
    } else if (!primaryFilterRef.current || !newFilters[primaryFilterRef.current]) {
        const newPrimary = activeFilterFields[0];

        if (primaryFilterRef.current && primaryFilterRef.current !== newPrimary) {
            const oldPrimaryCache = filterCacheRef.current.get(`primary:${primaryFilterRef.current}`);
            if (oldPrimaryCache) {
                filterCacheRef.current.set(primaryFilterRef.current, oldPrimaryCache);
                filterCacheRef.current.delete(`primary:${primaryFilterRef.current}`);
            }
        }

        const newPrimaryCache = filterCacheRef.current.get(newPrimary);
        if (newPrimaryCache) {
            filterCacheRef.current.set(`primary:${newPrimary}`, newPrimaryCache);
            filterCacheRef.current.delete(newPrimary);
        }

        primaryFilterRef.current = newPrimary;
    }
}

export function useADWFilterHook() {

    const [triggerFieldFilter] = useGetFieldFilterMutation();
    const [activeFilters, setActiveFilters] = useState<Record<string, FilterModel>>({});
    const [currentFilter, setCurrentFilter] = useState<Record<string, FilterModel>>({});
    const filterCacheRef = useRef<Map<string, string[]>>(new Map());
    const primaryFilterRef = useRef<string | null>(null);
    const fieldContextRef = useRef<Map<string, string>>(new Map());

    const getFieldFilters = useCallback((params: any, field: string) => {
        const currentFilters = params.api.getFilterModel();
        setCurrentFilter(currentFilters);

        const isPrimaryFilter = field === primaryFilterRef.current;
        const cacheKey = isPrimaryFilter ? `primary:${field}` : field;

        const cachedValue = filterCacheRef.current.get(cacheKey);
        if (cachedValue) {
            return params.success(cachedValue);
        }

        let filterModelForAPI: { [p: string]: unknown };

        if (isPrimaryFilter) {
            filterModelForAPI = currentFilters;
        } else {
            filterModelForAPI = Object.fromEntries(
                Object.entries(currentFilters).filter(([key]) => key !== field)
            );
        }

        triggerFieldFilter({
            filterModel: filterModelForAPI,
            field: field
        })
            .unwrap()
            .then(response => {
                let finalOptions: string[];

                if (isPrimaryFilter) {
                    finalOptions = response;
                } else {
                    const existingSelections = currentFilters[field]?.values || [];
                    finalOptions = [...Array.from(new Set([...response, ...existingSelections]))];
                    const contextSignature = JSON.stringify(filterModelForAPI);
                    fieldContextRef.current.set(field, contextSignature);
                }
                filterCacheRef.current.set(cacheKey, finalOptions);
                params.success(finalOptions);
            })
            .catch(error => {
                console.error("Error fetching field filters:", error);
                params.success([]);
            });
    }, [triggerFieldFilter]);

    const updateCachedFieldFilters = useCallback(async (changedFields: string[], newFilters: Record<string, FilterModel>) => {
        changedFields.forEach(changedField => {
            const keysToDelete: string[] = [];
            fieldContextRef.current.forEach((contextSignature, field) => {
                const isFieldShouldBeDeleted = field !== changedField && field !== primaryFilterRef.current && contextSignature.includes(`"${changedField}"`);
                if (isFieldShouldBeDeleted) {
                    keysToDelete.push(field);
                }
            });

            keysToDelete.forEach(field => {
                filterCacheRef.current.delete(field);
                fieldContextRef.current.delete(field);
            });
        });

        const cachedFields = Array.from(filterCacheRef.current.keys())
            .filter(key => !key.startsWith('primary:'))
            .filter(field => !changedFields.includes(field) && field !== primaryFilterRef.current);

        if (cachedFields.length === 0) return;

        const updatePromises = cachedFields.map(async (field: string) => {
            try {
                const contextFilters = Object.fromEntries(
                    Object.entries(newFilters).filter(([key]) => key !== field)
                );

                const response = await triggerFieldFilter({
                    filterModel: contextFilters,
                    field: field
                }).unwrap();

                const existingSelections = newFilters[field]?.values || [];
                const mergedOptions = [...Array.from(new Set([...response, ...existingSelections]))];

                filterCacheRef.current.set(field, mergedOptions);
                fieldContextRef.current.set(field, JSON.stringify(contextFilters));

                return { field, values: mergedOptions, success: true };
            } catch {
                return { field, values: [], success: false };
            }
        });

        await Promise.all(updatePromises);
    }, [triggerFieldFilter]);

    const onFilterChange = useCallback(async (event: any) => {
        const filters = event.api.getFilterModel();
        createPrimaryFilter(filters, primaryFilterRef, filterCacheRef);
        if (Object.keys(currentFilter).length > 0) {
            const changedFields = compareFilterValues(currentFilter, filters);
            if (changedFields.length > 0) {
                await updateCachedFieldFilters(changedFields, filters);
            }
        }

        setCurrentFilter(filters);
        setActiveFilters(filters);
    }, [currentFilter, updateCachedFieldFilters]);

    const clearAllFilters = useCallback((gridRefParam?: AgGridReact | null) => {
        filterCacheRef.current.clear();
        fieldContextRef.current.clear();
        primaryFilterRef.current = null;
        gridRefParam?.api?.setFilterModel(null);
        setActiveFilters({});
        setCurrentFilter({});
    }, []);

    const clearFieldFilter = useCallback((field: string, gridRefParam: AgGridReact | null) => {
        gridRefParam?.api?.destroyFilter(field);
        gridRefParam?.api?.onFilterChanged();
        filterCacheRef.current.delete(field);
        filterCacheRef.current.delete(`primary:${field}`);
        fieldContextRef.current.delete(field);
    }, []);

    return {
        activeFilters,
        onFilterChange,
        getFieldFilters,
        clearAllFilters,
        clearFieldFilter
    }
}